import React, { ReactElement, FC, PropsWithChildren, Suspense, useMemo } from 'react';
import { Spinner } from '@placer-ui/components';
import { getFeatureFlags } from 'core/flow-control';
import { useHttpClientFactory } from '../../../../infra/react/hooks/useHttpClientFactory';
import { createPlacerAssistantApp } from 'features/pi/services/createPlacerAssistantApp';
import { DrawerController, IDrawerController } from 'features/pi/view/drawer/DrawerController';
import {
    AssistantUiManager,
    IAssistantUiManager,
} from 'features/pi/view/uiManager/AssistantUiManager';
import { ErrorBoundary } from 'react-error-boundary';
import { PlacerAssistantContextProvider } from 'features/pi/view/hooks/PlacerAssistantContextProvider';
import { AssistantPanel } from 'features/pi/view/AssistantPanel';
import { useServerSentEventsFactoryCreator } from '../../../../infra';
import DrawerContainer from 'features/pi/view/drawer/side-panel-drawer/DrawerContainer';

export const DesktopPiDrawerContainer: FC<
    PropsWithChildren<{ controller?: IDrawerController; uiManager?: IAssistantUiManager }>
> = ({ children, controller, uiManager }) => {
    const drawerController = useMemo(() => controller || new DrawerController(), [controller]);
    const drawerUiManager = useMemo(
        () => uiManager || new AssistantUiManager(drawerController),
        [drawerController, uiManager],
    );

    const httpClientFactory = useHttpClientFactory();
    const serverSentEventsFactoryCreator = useServerSentEventsFactoryCreator();
    const { enable_pi_in_the_platform } = getFeatureFlags();
    const placerAssistantApp = useMemo(() => {
        const httpClient = httpClientFactory.createClientWithBaseUrl(
            process.env.REACT_APP_PI_BASE_URL!,
        );
        const serverSentEventsFactory = serverSentEventsFactoryCreator.createFactory(
            process.env.REACT_APP_PI_BASE_URL!,
        );

        return createPlacerAssistantApp(httpClient, serverSentEventsFactory);
    }, [httpClientFactory, serverSentEventsFactoryCreator]);

    let returnComponent: ReactElement | null = React.isValidElement(children) ? children : null;
    if (enable_pi_in_the_platform) {
        returnComponent = (
            <>
                <ErrorBoundary fallback={<div>Something went wrong</div>}>
                    <PlacerAssistantContextProvider
                        {...placerAssistantApp}
                        assistantUiManager={drawerUiManager}
                    >
                        <DrawerContainer
                            drawerSize={'480px'}
                            drawerContent={
                                <Suspense fallback={<Spinner variant={'brand'} />}>
                                    <AssistantPanel />
                                </Suspense>
                            }
                            drawerUiManager={drawerUiManager}
                            drawerController={drawerController}
                        >
                            {children}
                        </DrawerContainer>
                    </PlacerAssistantContextProvider>
                </ErrorBoundary>
            </>
        );
    }
    return returnComponent;
};
